import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import { MutationConfig } from '@/lib/tanstack-query';
import { NameSchema } from '@/validators/shared/utility/name.schema';
import { listSalesQueryOptions } from './list-sales';
import { listGroupsQueryOptions } from '@/features/groups/api/list-groups';

const SaleItemInputSchema = z.object({
  productId: z.string().uuid(),
  quote: z.string().nonempty(),
});

export type SaleItemInput = z.infer<typeof SaleItemInputSchema>;

const dedupItems = (items: SaleItemInput[]) => items.length === new Set(items.map((i) => i.productId)).size;

export const CreateSaleInputSchema = z.object({
  name: NameSchema,
  roundId: z.string().uuid(),
  expectedParticipationCount: z.number().int().min(5, 'Minst 5 krävs').max(100, 'Max 100 tillåtet'),
  participationItemCountTarget: z.number().int().min(1, 'Minst 1 krävs').max(100, 'Max 100 tillåtet'),
  items: z
    .array(SaleItemInputSchema)
    .refine(dedupItems, {
      message: 'Duplicerade productId är inte tillåtna',
      path: ['items'],
    })
    .optional(),
  bonusCode: z.string().optional(),
});

export type CreateSaleInput = z.infer<typeof CreateSaleInputSchema>;

export type CreateSaleResult = {
  id: string;
};

export const createSale = async ({ data }: { data: CreateSaleInput }): Promise<CreateSaleResult> => {
  return (await api.post(`/sales`, data)).data;
};

type UseCreateSaleOptions = {
  mutationConfig?: MutationConfig<typeof createSale>;
};

export const useCreateSale = ({ mutationConfig }: UseCreateSaleOptions = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: listSalesQueryOptions().queryKey });
      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: createSale,
  });
};
